import { useMemo } from "react";
import { useSelector } from "react-redux";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { RootState } from "../../../../../../../redux/store";
import ChartCard, { ChartEmptyState } from "./ChartCard";
import FlaggedIcon from "../../../../../../../assets/svg/flagged-icon";
import { PriorityChartItem } from "../utils/constants";
import { getHelpdeskLabels } from "../../../../../../../utils/constants/mappings";

const RADIAN = Math.PI / 180;

type LabelProps = {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  index: number;
  isDarkMode: boolean;
  data: PriorityChartItem[];
};

const SliceLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, index, isDarkMode, data }: LabelProps) => {
  const seg = data?.[index];
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <>
    {seg?.value > 4 && (
    <text
      x={x}
      y={y}
      fill={seg?.id === "na" ? isDarkMode ? "#FBFBFB" : "#6b7280" : "#ffffff"}
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={12}
      fontWeight={600}
    >
      {seg?.displayPercent}
    </text>
    )}
    </>
  );
};

interface PriorityDistributionChartProps {
  priorityData: PriorityChartItem[];
  totalTickets: number;
}

const PriorityDistributionChart = ({ priorityData, totalTickets }: PriorityDistributionChartProps) => {
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );
  const helpdeskLabels = getHelpdeskLabels(region ?? "UK");
  const isDarkMode = useSelector((state: RootState) => state?.themeProvider?.isDarkMode);
  const headingColor = isDarkMode ? "#fbfbfb" : "#252b37";
  const countColor   = isDarkMode ? "#c0c2c5" : "#636770";
  const naSliceColor = isDarkMode ? "#636770" : "#e2e8f0";

  const chartData = useMemo(
    () => priorityData?.map((d) => ({
      ...d,
      color: d?.id === "na" ? naSliceColor : d?.color,
    })),
    [priorityData, naSliceColor]
  );

  return (
    <ChartCard
      title="Priority Distribution"
      description={`Number of ${helpdeskLabels.ticketPluralLower} by priority level`}
      headerIcon={
        <FlaggedIcon className="w-4 h-4 text-verba-indigo dark:text-verba-indigo-dk" />
      }
      className="!gap-1 max-h-[286px] pb-1.5!"
    >
      {priorityData?.length === 0 ? (
        <ChartEmptyState />
      ) : (
      <div className="flex items-center justify-between w-full -mt-2">
        {/* Legend */}
        <div className="flex flex-col gap-2">
          {priorityData?.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between gap-2 rounded-[8px] px-3 py-2 border"
              style={{
                backgroundColor: isDarkMode ? item?.rowBgDk : item?.rowBg,
                borderColor: "transparent",
              }}
            >
              <div className="flex items-center gap-1.5 min-w-0">
                {item?.hasFlag && (
                  <span
                    className="shrink-0 flex"
                    style={{ color: item?.flagColor }}
                  >
                    <FlaggedIcon className="w-3.5 h-3.5" />
                  </span>
                )}
                <span
                  className="text-sm font-medium truncate"
                  style={{ color: headingColor }}
                >
                  {item?.label}
                </span>
              </div>

              <span
                className="text-xs shrink-0 whitespace-nowrap"
                style={{ color: countColor }}
              >
                {item.tickets?.toLocaleString()} {helpdeskLabels.ticketPlural}
              </span>
            </div>
          ))}
        </div>

        {/* Donut chart */}
        <div className="relative shrink-0 w-[222px] h-[222px]">
          <div className="relative z-10 w-full h-full">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <Pie
                data={chartData as unknown as Record<string, unknown>[]}
                dataKey="value"
                startAngle={160}
                endAngle={-270}
                innerRadius="58%"
                outerRadius="92%"
                stroke="none"
                paddingAngle={0}
                label={(props: Omit<LabelProps, "data">) => (
                  <SliceLabel {...props} isDarkMode={isDarkMode} data={priorityData} />
                )}
                labelLine={false}
                isAnimationActive={false}
              >
                {chartData?.map((seg) => (
                  <Cell key={seg?.id} fill={seg?.color} />
                ))}
              </Pie>
              <Tooltip
                offset={0}
                allowEscapeViewBox={{ x: true, y: true }}
                content={({ active, payload, coordinate }) => {
                  if (!active || !payload?.length) return null;
                  const hoveredSeg = payload?.[0]?.payload as PriorityChartItem;
                  const hoveredColor = hoveredSeg?.color;
                  const cursorX = coordinate?.x ?? 0;
                  const cursorY = coordinate?.y ?? 0;
                  return (
                    <div
                      className="w-[220px] flex flex-col bg-tooltip-bg dark:bg-tooltip-bg-dk py-2 text-white text-xs rounded-[6px] shadow-md pointer-events-none"
                      style={{
                        position: "absolute",
                        left: cursorX,
                        top: cursorY,
                        transform: "translate(-105%, -105%)",
                      }}
                    >
                      <h4 className="text-xs pb-1.5 px-3 text-pure-white dark:text-pure-white-dk">
                        Priority Distribution
                      </h4>
                      {priorityData?.map((item) => {
                        const isHovered = item?.color === hoveredColor;
                        return (
                          <div
                            key={item.id}
                            className={`grid items-center gap-x-2 px-2 py-1 grid-cols-[10px_1fr_auto_auto] ${
                              isHovered
                                ? "bg-analytics-tooltip-hover dark:bg-analytics-tooltip-hover-dk"
                                : "bg-transparent"
                            }`}
                          >
                            <FlaggedIcon className={`w-2.5 h-2.5 ${item?.id === "na" ? "opacity-0" : ""}`} style={{ color: item?.color }} />
                            <p className="text-xs text-pure-white dark:text-pure-white-dk truncate">
                              {item?.label}
                            </p>
                            <p className="text-xs text-right tabular-nums text-pure-white dark:text-pure-white-dk font-bold">
                              {item?.tickets?.toLocaleString()}
                            </p>
                            <p className="text-xs text-right tabular-nums font-medium text-gray-placeholder dark:text-gray-placeholder-dk">
                              {item?.displayPercent}
                            </p>
                          </div>
                        );
                      })}
                      <div className="grid items-center gap-x-2 px-2 py-1 mt-0.5 border-t border-white/10 grid-cols-[10px_1fr_auto_auto]">
                        <span className="w-2.5 h-2.5" />
                        <p className="text-xs text-pure-white dark:text-pure-white-dk">
                          Total
                        </p>
                        <p className="text-xs text-right tabular-nums text-pure-white dark:text-pure-white-dk font-bold">
                          {totalTickets?.toLocaleString()}
                        </p>
                        <p className="text-xs text-right tabular-nums font-medium text-gray-placeholder dark:text-gray-placeholder-dk">
                          100%
                        </p>
                      </div>
                    </div>
                  );
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          </div>

          <div className="absolute inset-0 z-0 flex flex-col items-center justify-center pointer-events-none">
            <p
              className="text-xl font-medium leading-none"
              style={{ color: headingColor }}
            >
              {totalTickets?.toLocaleString()}
            </p>
            <p
              className="text-sm leading-none mt-1.5 text-center whitespace-nowrap"
              style={{ color: countColor }}
            >
              Total {helpdeskLabels.ticketPlural}
            </p>
          </div>
        </div>
      </div>
      )}
    </ChartCard>
  );
};

export default PriorityDistributionChart;
